import React, {useState} from 'react';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {observer} from 'mobx-react-lite';

import OrderDetails from '../../../components/checkout/OrderDetails';
import InputModal from '../../../components/checkout/OrderDetails/InputModal';
import OrderNotes from '../../../components/checkout/OrderDetails/OrderNotes';
import PickupDeliverComponent from '../../../components/pickupDeliver/PickupDeliverComponent';
import SafeAreaPadding from '../../../components/SafeAreaPadding';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import CouponBlockedNotice from '../components/CouponBlockedNotice';
import OrderTotals from '../components/OrderTotals';
import {useCheckoutSession} from '../hooks';
import UnifiedCheckoutScreen from '../UnifiedCheckout';
import {handleContinue} from '../util/validation';

const CheckoutScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT>) => {
    // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
    // React requires hooks to be called in the same order on every render

    // Session management: login reload + empty cart redirect
    useCheckoutSession({step: 'checkout'});

    const {cartStore, userStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding();
    const [isOrderNotesModalVisible, setIsOrderNotesModalVisible] = useState(false);

    // Web desktop uses unified checkout layout
    if (isWebDesktopScreen) {
      return <UnifiedCheckoutScreen step="checkout" navigation={navigation} />;
    }

    // Mobile uses existing implementation below

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const saveOrderNote = (value: any) => {
      cartStore.setOrderNotes(value);
      cartStore.updateOrderNotes();
      setIsOrderNotesModalVisible(false);
    };

    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={{paddingBottom: tabBarPadding}}
          enableOnAndroid
          extraScrollHeight={40}>
          <PickupDeliverComponent />
          <OrderDetails />
          <InputModal
            element={({onPress}) => (
              <OrderNotes
                onPress={onPress}
                notes={cartStore.orderNotes || 'Add note to store'}
                testID="checkout-screen-order-notes"
              />
            )}
            isVisible={isOrderNotesModalVisible}
            title="Add note to store"
            saveAction={saveOrderNote}
            saveText="UPDATE"
            closeAction={() => setIsOrderNotesModalVisible(false)}
            openAction={() => setIsOrderNotesModalVisible(true)}
            initialValue={cartStore.orderNotes}
            label="Order note"
            helperText="Add any special instructions for the store"
            placeholder="e.g. Please leave at door"
          />
          <View style={{flexGrow: 1}} />
          <OrderTotals>
            <Button
              onPress={handleContinue({
                cartStore,
                userStore,
                skipServerValidation: true,
                callback: () => {
                  navigation.navigate(AppRoute.CART_ORDER_CHECKOUT_REVIEW);
                },
              })}
              disabled={cartStore.couponBlocksCheckout}
              title={
                cartStore.couponBlocksCheckout ? 'ADD MORE TO CONTINUE WITH COUPON' : 'REVIEW ORDER'
              }
            />
            <CouponBlockedNotice />
          </OrderTotals>
        </KeyboardAwareScrollView>
        <SafeAreaPadding bottom />
      </ContentView>
    );
  },
);

export default CheckoutScreen;
